import { ref } from 'vue';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';

export interface UseDrawLayerReorderOptions {
  map: NoraMap;
  /**
   * Optional guard limiting which draw layers may be reordered relative to each other
   * (e.g. only within the same section). Returning `false` rejects the drop.
   */
  canReorder?: (draggedDrawLayerId: string, targetDrawLayerId: string) => boolean;
}

/**
 * HTML5 drag-and-drop reordering for the draw layers in the overlay menu.
 * Tracks the dragged item, the current drop target, and whether the drop would land
 * above or below that target, and commits the reorder via the map's draw state.
 */
export function useDrawLayerReorder(options: UseDrawLayerReorderOptions) {
  const { map, canReorder } = options;

  const draggedItemId = ref<string | null>(null);
  const dragOverItemId = ref<string | null>(null);
  const dragDirection = ref<'above' | 'below' | null>(null);

  function reset() {
    draggedItemId.value = null;
    dragOverItemId.value = null;
    dragDirection.value = null;
  }

  function handleDragStart(drawLayerId: string, event: DragEvent) {
    draggedItemId.value = drawLayerId;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', drawLayerId);
    }
  }

  function handleDragOver(drawLayerId: string, event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (
      draggedItemId.value === null
      || draggedItemId.value === drawLayerId
      || (canReorder !== undefined && !canReorder(draggedItemId.value, drawLayerId))
    ) {
      // Reject drops from another section: clear any indicator and show a "no-drop" cursor.
      dragOverItemId.value = null;
      dragDirection.value = null;

      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }

      return;
    }

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }

    // Determine if we're dragging above or below the target
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const midPoint = rect.top + rect.height / 2;

    dragOverItemId.value = drawLayerId;
    dragDirection.value = event.clientY < midPoint ? 'above' : 'below';
  }

  function handleDragLeave(event: DragEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement | null;

    if (relatedTarget === null || !(event.currentTarget as HTMLElement).contains(relatedTarget)) {
      dragOverItemId.value = null;
      dragDirection.value = null;
    }
  }

  function handleDrop(targetDrawLayerId: string, event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (
      draggedItemId.value === null
      || draggedItemId.value === targetDrawLayerId
      || (canReorder !== undefined && !canReorder(draggedItemId.value, targetDrawLayerId))
    ) {
      reset();

      return;
    }

    map.state.draw.reorderDrawLayers(draggedItemId.value, targetDrawLayerId);

    reset();
  }

  function handleDragEnd() {
    reset();
  }

  return {
    draggedItemId,
    dragOverItemId,
    dragDirection,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  };
}
